import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2, RefreshCw, ZoomIn } from 'lucide-react';
import { useEvent, useLazyCommand } from '@soap/modules';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { RegenerateFigureDialog } from '../dialogs/RegenerateFigureDialog';
import { AiGeneratedFigureFrame } from './AiGeneratedFigureFrame';
import { EnlargedMediaZoom } from './EnlargedMediaZoom';
import { copySvgFigureToClipboard } from './copySvgFigure';
import { figureDiagramAriaLabel, parseFigureDiagramSpec } from './figureDiagramSpec';
import { InlineSvgFigure } from './InlineSvgFigure';
import { FigureGenerationFailureActions } from './FigureGenerationFailureActions';
import { normalizeFigureKey, normalizeIdPart } from './loadingStateKeys';
import { resolveStepIdForMediaCommand } from './mediaReviewUtils';
import { useSessionDebugMode } from '../../hooks/useSessionDebugMode';

const E001_EVENT_NAME = 'Soap.Interfaces.Messages.E001v1_MessageFailed, Soap.Interfaces.Messages';
const REGENERATE_TIMEOUT_MS = 120_000;

export type LessonFigureRegenerateContext = {
  unitId: string;
  lessonId: string;
  materialId: string;
  onMaterialContentUpdated?: (content: string) => void;
  onAcceptTextOnly?: (figureKey: string) => void;
  isAcceptingTextOnly?: (figureKey: string) => boolean;
  hostMaterialKind?: 'worksheet' | 'reading';
  onOpenRegenerateHostMaterial?: () => void;
  onOpenRegenerateLesson?: () => void;
  onOpenRegenerateUnit?: () => void;
};

type LessonFigureDiagramProps = {
  figureKey: string;
  svg: string;
  spec?: string;
  failed: boolean;
  canRegenerate: boolean;
  regenerateContext?: LessonFigureRegenerateContext;
  domId?: string;
};

export function LessonFigureDiagram({
  figureKey,
  svg,
  spec,
  failed,
  canRegenerate,
  regenerateContext,
  domId,
}: LessonFigureDiagramProps) {
  const [currentSvg, setCurrentSvg] = useState(svg);
  const [isFailed, setIsFailed] = useState(failed);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [enlargedOpen, setEnlargedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [sendRegenerate] = useLazyCommand('C333v1_RegenerateLessonFigure');
  const sessionDebugMode = useSessionDebugMode();
  const pendingConversationIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSvg = Boolean(currentSvg?.trim());
  const normalizedFigureKey = normalizeFigureKey(figureKey);
  const diagramSpec = parseFigureDiagramSpec(spec);
  const ariaLabel = figureDiagramAriaLabel(diagramSpec, normalizedFigureKey);

  const clearRegenerateTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const finishRegenerating = (errorMessage?: string | null) => {
    clearRegenerateTimeout();
    pendingConversationIdRef.current = null;
    setIsRegenerating(false);
    setRegenerateError(errorMessage ?? null);
  };

  useEffect(() => {
    setCurrentSvg(svg);
    setIsFailed(failed);
  }, [svg, failed]);

  useEffect(() => () => clearRegenerateTimeout(), []);

  const extractFigureSvg = (content: string, key: string): string | null => {
    const escaped = normalizeFigureKey(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const blockRe = new RegExp(
      `<!--\\s*FIGURE_DIAGRAM:key=${escaped}\\s*-->[\\s\\S]*?<!--\\s*/FIGURE_DIAGRAM\\s*-->`,
      'i'
    );
    const block = content.match(blockRe)?.[0];
    return block?.match(/<svg\b[\s\S]*?<\/svg>/i)?.[0] ?? null;
  };

  const handleRegeneratedEvent = (event: {
    e333_UnitId?: string;
    E333_UnitId?: string;
    e333_LessonId?: string;
    E333_LessonId?: string;
    e333_MaterialId?: string;
    E333_MaterialId?: string;
    e333_FigureKey?: string;
    E333_FigureKey?: string;
    e333_MaterialContent?: string;
    E333_MaterialContent?: string;
    e333_Failed?: boolean;
    E333_Failed?: boolean;
  }) => {
    if (!regenerateContext) return;

    const unitId = event.e333_UnitId ?? event.E333_UnitId;
    const lessonId = event.e333_LessonId ?? event.E333_LessonId;
    const materialId = event.e333_MaterialId ?? event.E333_MaterialId;
    const key = event.e333_FigureKey ?? event.E333_FigureKey;
    const content = event.e333_MaterialContent ?? event.E333_MaterialContent;
    const eventFailed = event.e333_Failed ?? event.E333_Failed ?? false;

    const eventStepId = resolveStepIdForMediaCommand(
      materialId != null ? String(materialId) : undefined
    );
    const contextStepId = resolveStepIdForMediaCommand(regenerateContext.materialId);

    if (
      normalizeIdPart(unitId) !== normalizeIdPart(regenerateContext.unitId) ||
      normalizeIdPart(lessonId) !== normalizeIdPart(regenerateContext.lessonId) ||
      normalizeIdPart(eventStepId) !== normalizeIdPart(contextStepId) ||
      normalizeFigureKey(key ?? '') !== normalizedFigureKey
    ) {
      return;
    }

    finishRegenerating(null);
    if (content) {
      regenerateContext.onMaterialContentUpdated?.(content);
      const nextSvg =
        extractFigureSvg(content, normalizedFigureKey) ??
        content.match(/<svg\b[\s\S]*?<\/svg>/i)?.[0];
      if (nextSvg) {
        setCurrentSvg(nextSvg);
        setIsFailed(/data-diagram-failed\s*=\s*["']true["']/i.test(nextSvg) || Boolean(eventFailed));
      }
    }
  };

  useEvent({
    eventName: 'E441v1_LessonFigureTextOnlyAccepted',
    onEventReceived: (event: {
      e441_UnitId?: string;
      E441_UnitId?: string;
      e441_LessonId?: string;
      E441_LessonId?: string;
      e441_MaterialId?: string;
      E441_MaterialId?: string;
      e441_FigureKey?: string;
      E441_FigureKey?: string;
      e441_MaterialContent?: string;
      E441_MaterialContent?: string;
    }) => {
      if (!regenerateContext) return;
      const unitId = event.e441_UnitId ?? event.E441_UnitId;
      const lessonId = event.e441_LessonId ?? event.E441_LessonId;
      const materialId = event.e441_MaterialId ?? event.E441_MaterialId;
      const key = event.e441_FigureKey ?? event.E441_FigureKey;
      const content = event.e441_MaterialContent ?? event.E441_MaterialContent;
      const eventStepId = resolveStepIdForMediaCommand(
        materialId != null ? String(materialId) : undefined
      );
      const contextStepId = resolveStepIdForMediaCommand(regenerateContext.materialId);
      if (
        normalizeIdPart(unitId) !== normalizeIdPart(regenerateContext.unitId) ||
        normalizeIdPart(lessonId) !== normalizeIdPart(regenerateContext.lessonId) ||
        normalizeIdPart(eventStepId) !== normalizeIdPart(contextStepId) ||
        normalizeFigureKey(key ?? '') !== normalizedFigureKey
      ) {
        return;
      }
      if (content) {
        regenerateContext.onMaterialContentUpdated?.(content);
        const nextSvg =
          extractFigureSvg(content, normalizedFigureKey) ??
          content.match(/<svg\b[\s\S]*?<\/svg>/i)?.[0];
        if (nextSvg) {
          setCurrentSvg(nextSvg);
          setIsFailed(false);
        }
      }
    },
  });

  useEvent({
    eventName: 'E333v1_LessonFigureRegenerated',
    onEventReceived: handleRegeneratedEvent,
  });

  useEvent({
    eventName: E001_EVENT_NAME,
    onEventReceived: (event: { e001_MessageId?: string; e001_ErrorMessage?: string }) => {
      if (!pendingConversationIdRef.current) return;
      if (event.e001_MessageId && event.e001_MessageId !== pendingConversationIdRef.current) return;
      finishRegenerating(event.e001_ErrorMessage ?? 'Could not regenerate this diagram.');
    },
  });

  const handleRegenerate = (revisionNotes?: string) => {
    if (!regenerateContext || !canRegenerate || isRegenerating) return;

    setRegenerateError(null);
    setIsRegenerating(true);

    const stepId = resolveStepIdForMediaCommand(regenerateContext.materialId);
    if (!stepId) {
      finishRegenerating('Could not resolve the instruction step for this figure.');
      return;
    }

    const conversationId = sendRegenerate({
      C333_UnitId: regenerateContext.unitId,
      C333_LessonId: regenerateContext.lessonId,
      C333_MaterialId: stepId,
      C333_FigureKey: normalizedFigureKey,
      ...(revisionNotes ? { C333_RevisionNotes: revisionNotes } : {}),
    });

    if (!conversationId) {
      finishRegenerating('Could not send regenerate request. Try again in a moment.');
      return;
    }

    pendingConversationIdRef.current = conversationId;
    clearRegenerateTimeout();
    timeoutRef.current = setTimeout(() => {
      if (pendingConversationIdRef.current === conversationId) {
        finishRegenerating('Diagram regeneration timed out. Check your connection and try again.');
      }
    }, REGENERATE_TIMEOUT_MS);
  };

  const handleCopy = async () => {
    if (!hasSvg || isRegenerating) return;

    setCopyError(null);
    try {
      await copySvgFigureToClipboard(currentSvg);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError('Could not copy this diagram.');
    }
  };

  return (
    <div id={domId} className="my-3 rounded-md border border-gray-200 bg-white p-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-gray-500">{figureKey}</span>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => void handleCopy()}
            disabled={!hasSvg || isRegenerating}
            aria-label={`Copy ${figureKey}`}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-confirm-action" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={() => setEnlargedOpen(true)}
            disabled={!hasSvg}
            aria-label={`Enlarge ${figureKey}`}
          >
            <ZoomIn className="h-3.5 w-3.5" />
            Enlarge
          </Button>
          {canRegenerate && regenerateContext && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => setRegenerateDialogOpen(true)}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {isRegenerating ? 'Regenerating Image…' : 'Regenerate Image'}
            </Button>
          )}
        </div>
      </div>
      {isRegenerating && (
        <p className="mb-2 text-xs text-gray-600">Generating a new diagram — this can take up to a minute.</p>
      )}
      {regenerateError && (
        <p className="mb-2 text-xs text-destructive-action">{regenerateError}</p>
      )}
      {copyError && (
        <p className="mb-2 text-xs text-destructive-action">{copyError}</p>
      )}
      {isFailed && !isRegenerating && (
        <div className="mb-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-medium text-amber-950">We were unable to generate this image.</p>
          {diagramSpec.description ? (
            <p>
              <span className="font-semibold">Purpose:</span> {diagramSpec.description}
            </p>
          ) : null}
          <FigureGenerationFailureActions
            className="text-amber-900"
            hostMaterialKind={regenerateContext?.hostMaterialKind}
            onOpenRegenerateHostMaterial={regenerateContext?.onOpenRegenerateHostMaterial}
            onOpenRegenerateLesson={regenerateContext?.onOpenRegenerateLesson}
          />
        </div>
      )}
      <figure>
        <AiGeneratedFigureFrame className="block w-full">
          <InlineSvgFigure svg={currentSvg} ariaLabel={ariaLabel} />
        </AiGeneratedFigureFrame>
        {sessionDebugMode && diagramSpec && (
          <details className="mt-2 text-xs text-gray-600">
            <summary className="cursor-pointer select-none text-gray-500 hover:text-gray-700">
              Diagram generation prompt
            </summary>
            <dl className="mt-1 space-y-1 rounded border border-gray-100 bg-gray-50 p-2 text-gray-700">
              {diagramSpec.description && (
                <div>
                  <dt className="font-medium text-gray-500">Focus sent to model</dt>
                  <dd className="whitespace-pre-wrap">{diagramSpec.description}</dd>
                </div>
              )}
              {diagramSpec.imageType && (
                <div>
                  <dt className="font-medium text-gray-500">Diagram type</dt>
                  <dd>{diagramSpec.imageType}</dd>
                </div>
              )}
              {diagramSpec.figureRole && (
                <div>
                  <dt className="font-medium text-gray-500">Figure role</dt>
                  <dd>{diagramSpec.figureRole}</dd>
                </div>
              )}
              {diagramSpec.contentDescription && (
                <div>
                  <dt className="font-medium text-gray-500">Extracted description</dt>
                  <dd className="whitespace-pre-wrap">{diagramSpec.contentDescription}</dd>
                </div>
              )}
              {diagramSpec.contentJson && (
                <div>
                  <dt className="font-medium text-gray-500">Extracted content</dt>
                  <dd className="whitespace-pre-wrap font-mono text-[11px] leading-snug">
                    {diagramSpec.contentJson}
                  </dd>
                </div>
              )}
            </dl>
          </details>
        )}
      </figure>
      <Dialog open={enlargedOpen} onOpenChange={setEnlargedOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{figureKey}</DialogTitle>
          </DialogHeader>
          <EnlargedMediaZoom resetToken={enlargedOpen}>
            <AiGeneratedFigureFrame className="block w-full">
              <InlineSvgFigure
                svg={currentSvg}
                ariaLabel={ariaLabel}
                className="my-0 max-w-none overflow-visible [&_svg]:max-h-none [&_svg]:w-full"
              />
            </AiGeneratedFigureFrame>
          </EnlargedMediaZoom>
        </DialogContent>
      </Dialog>
      {regenerateDialogOpen && canRegenerate && regenerateContext ? (
        <RegenerateFigureDialog
          figureKey={normalizedFigureKey || figureKey}
          onClose={() => setRegenerateDialogOpen(false)}
          onConfirm={notes => {
            handleRegenerate(notes || undefined);
          }}
        />
      ) : null}
    </div>
  );
}
